import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtTL, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { AlertOctagon, CalendarClock, ArrowRight } from "lucide-react";

const daysLeft = (iso) => Math.ceil((new Date(iso).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000);

const Row = ({ title, sub, amount, due, overdue, tone }) => {
  const dl = daysLeft(due);
  const when = overdue ? `${Math.abs(dl)} gün gecikti` : dl === 0 ? "Bugün" : dl === 1 ? "Yarın" : `${dl} gün kaldı`;
  return (
    <div className={`flex items-center justify-between gap-3 p-2.5 rounded border text-sm ${overdue ? "bg-red-500/10 border-red-500/30" : "bg-amber-500/5 border-amber-500/20"}`}>
      <div className="min-w-0">
        <div className="font-medium truncate">{title}</div>
        <div className="text-xs text-muted-foreground">{sub} · {fmtDate(due)}</div>
      </div>
      <div className="text-right shrink-0">
        <div className={`num font-bold ${tone}`}>{fmtTL(amount)}</div>
        <div className={`text-[11px] font-semibold ${overdue ? "text-red-500" : "text-amber-500"}`}>{when}</div>
      </div>
    </div>
  );
};

export const DueAlerts = () => {
  const [col, setCol] = useState([]);
  const [pay, setPay] = useState([]);

  useEffect(() => {
    api.get("/upcoming/collections", { params: { days: 7 } }).then((r) => setCol(r.data)).catch(() => {});
    api.get("/upcoming/payments", { params: { days: 7 } }).then((r) => setPay(r.data)).catch(() => {});
  }, []);

  const overdueCount = col.filter((x) => x.overdue).length + pay.filter((x) => x.overdue).length;
  if (col.length === 0 && pay.length === 0) return null;

  return (
    <Card className={`p-6 border ${overdueCount ? "border-red-500/40" : "border-amber-500/30"}`} data-testid="due-alerts">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          {overdueCount ? <AlertOctagon size={18} className="text-red-500" /> : <CalendarClock size={18} className="text-amber-500" />}
          <h3 className="font-bold text-lg">Vade Uyarıları</h3>
          {overdueCount > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-500 text-white" data-testid="overdue-count">{overdueCount} gecikmiş</span>}
          <span className="text-xs text-muted-foreground">(7 gün içinde)</span>
        </div>
        <Link to="/yaklasan" className="text-xs text-primary hover:underline flex items-center gap-1">Tümü <ArrowRight size={12} /></Link>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-2" data-testid="due-collections">
          <div className="text-xs uppercase font-semibold tracking-wider text-muted-foreground">Alacak Tahsilatları ({col.length})</div>
          {col.slice(0, 5).map((x) => <Row key={x.id} title={x.customer_name} sub="Satış vadesi" amount={x.amount} due={x.due_date} overdue={x.overdue} tone="text-primary" />)}
          {col.length === 0 && <div className="text-sm text-muted-foreground py-2">Yaklaşan tahsilat yok</div>}
        </div>
        <div className="space-y-2" data-testid="due-payments">
          <div className="text-xs uppercase font-semibold tracking-wider text-muted-foreground">Yapılacak Ödemeler ({pay.length})</div>
          {pay.slice(0, 5).map((x) => <Row key={x.id} title={x.category} sub={x.note || "Gider"} amount={x.amount} due={x.due_date} overdue={x.overdue} tone="text-red-400" />)}
          {pay.length === 0 && <div className="text-sm text-muted-foreground py-2">Yaklaşan ödeme yok</div>}
        </div>
      </div>
    </Card>
  );
};
